import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Download, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { buildRevenueCsv, downloadCsv } from "@/lib/revenueCsv";

const usd = (n) => `$${Number(n || 0).toFixed(2)}`;
const monthName = (m) => new Date(`${m}-01T00:00:00Z`).toLocaleString(undefined, { month: "long", timeZone: "UTC" });

const MonthRow = ({ r }) => (
  <div className="flex items-center justify-between border-t border-[#2D3442] py-2 text-sm" data-testid={`revenue-record-${r.month}`}>
    <span className="w-32 text-[#F3EBDD]">{monthName(r.month)}</span>
    <span className="w-24 text-[#A8A090]">{usd(r.income?.net)}</span>
    <span className="w-24 text-xs text-[#A8A090]">{r.final ? "Closed" : "In progress"}</span>
    <Button variant="ghost" size="sm" onClick={() => downloadCsv(`loreright-revenue-${r.month}.csv`, buildRevenueCsv(`LoreRight revenue report — ${monthName(r.month)} ${r.month.slice(0, 4)}`, [r]))}
      data-testid={`revenue-month-csv-${r.month}`}><Download className="mr-1 h-3.5 w-3.5" />CSV</Button>
  </div>
);

const YearBlock = ({ year, rows, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
  <div className="rounded-md border border-[#2D3442] p-4" data-testid={`revenue-year-${year}`}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-1 text-[#F3EBDD]" data-testid={`revenue-year-toggle-${year}`}>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "" : "-rotate-90"}`} />{year} <span className="ml-2 text-xs text-[#A8A090]">Net {usd(rows.reduce((a, r) => a + (r.income?.net || 0), 0))}</span></button>
      <Button size="sm" variant="outline" data-testid={`revenue-year-csv-${year}`}
        onClick={() => downloadCsv(`loreright-revenue-${year}-year-end.csv`, buildRevenueCsv(`LoreRight year-end revenue report — ${year} (January 1 – December 31)`, rows))}>
        <FileSpreadsheet className="mr-1 h-3.5 w-3.5" />Year-end report</Button>
    </div>
    {open && <div className="mt-3">{rows.map((r) => <MonthRow key={r.month} r={r} />)}</div>}
  </div>
  );
};

export const RevenueRecords = () => {
  const { data, error, isLoading } = useQuery({ queryKey: ["admin-revenue-records"], queryFn: () => api.get("/admin/revenue/records").then((r) => r.data) });
  const years = {};
  (data || []).forEach((r) => { (years[r.month.slice(0, 4)] ||= []).push(r); });
  return (
    <section className="rounded-md border border-[#2D3442] bg-[#0F131A] p-6" data-testid="revenue-records">
      <div className="lf-label">Bookkeeping records</div>
      <p className="mt-2 text-xs text-[#A8A090]">A record is saved for every month and kept for 4 years. Subscriber numbers are captured daily, so each closed month shows its end-of-month figures. Download one month, or a year-end report (January 1 – December 31) for taxes.</p>
      {isLoading && <p className="mt-4 text-sm text-[#A8A090]">Loading records…</p>}
      {error && <p className="mt-4 text-sm text-red-400" data-testid="revenue-records-error">{errMsg(error)}</p>}
      <div className="mt-4 space-y-4">
        {Object.keys(years).sort().reverse().map((y, i) => <YearBlock key={y} year={y} rows={years[y]} defaultOpen={i === 0} />)}
      </div>
    </section>
  );
};
